import { auth, functions, db } from '../firebase';
import { httpsCallable } from 'firebase/functions';
import { doc as firestoreDoc, runTransaction, serverTimestamp } from 'firebase/firestore';
import { getCypAdhdReportContentPathSegments } from '../constants/cypAdhdFirestorePaths';
import { STAGE_TO_REPORT_PREFIX, STAGE_KEY_TO_CYP_REPORT_STAGE } from '../constants/cypAdhdStages';
import { appendReportAuditEntry } from './reportAuditHelpers';

/**
 * Regenerate ALL AI paragraphs for one CYP ADHD assessment stage.
 *
 * CYP analogue of TriggerRegenerateAutismStage (same contract, CYP paths and
 * report stage names). It exists for the "forms changed after generation"
 * case: a parent/carer or school representative updates their pre-assessment
 * questionnaires AFTER the clinician has already generated a stage's
 * paragraphs, and the clinician makes no further proforma edits. Without an
 * explicit trigger nothing would re-run, leaving the report built on stale
 * inputs.
 *
 * Behaviour (mirrors the autism utility):
 *  - Clears every paragraph approval AND the stage-level approval flag on the
 *    reportContent/{stageKey} subcollection doc, so all paragraphs return to
 *    "needs review" (an approval never carries across regenerated content —
 *    ASMT-19).
 *  - Calls GenerateDraftWithLLM once per section with
 *    force_regenerate_keys: ['__ALL__'].
 *  - Awaits all generations so the caller can show a spinner.
 *
 * Deliberately does NOT touch `${prefix}ReportProcessingStatus` or the
 * `documents.*` rendered-PDF metadata — same reasoning as the autism utility
 * (LLM regeneration is not a document render).
 *
 * @param {Object}   params
 * @param {string}   params.patientId      Firestore patient document ID.
 * @param {string}   params.clinicianId    UID of the clinician initiating it.
 * @param {string}   params.stageKey       CYP stage key (e.g. 'clinicalInterview').
 * @param {string[]} params.sectionTitles  Report section titles to regenerate
 *                                          (must match the backend
 *                                          SECTION_PARAGRAPHS keys).
 * @param {string[]} [params.conditions]   Conditional flags.
 * @returns {Promise<{success: boolean, error?: any}>}
 */

// Shared with the stage-exit generation path — see the comment on
// STAGE_KEY_TO_CYP_REPORT_STAGE for why this is one map and not two.
const STAGE_KEY_TO_REPORT_STAGE = STAGE_KEY_TO_CYP_REPORT_STAGE;

export const TriggerRegenerateCypAdhdStage = async ({
  patientId,
  clinicianId,
  stageKey,
  sectionTitles,
  conditions = [],
}) => {
  const user = auth.currentUser;
  if (!user || !user.uid) return { success: false, error: 'User not authenticated' };

  const reportStage = STAGE_KEY_TO_REPORT_STAGE[stageKey];
  const prefix = STAGE_TO_REPORT_PREFIX[stageKey];

  if (
    !patientId ||
    !clinicianId ||
    !reportStage ||
    !prefix ||
    !Array.isArray(sectionTitles) ||
    sectionTitles.length === 0
  ) {
    return { success: false, error: 'Missing or invalid parameters' };
  }

  const reportContentRef = firestoreDoc(
    db,
    ...getCypAdhdReportContentPathSegments(patientId, stageKey),
  );
  const GenerateDraftWithLLM = httpsCallable(functions, 'GenerateDraftWithLLM');

  const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  // Small retry wrapper mirroring the autism utility: a short initial delay
  // lets the approval-reset write settle, and transient failures are retried
  // with linear backoff. deadline-exceeded is rethrown immediately because
  // the generation likely still completed server-side.
  const callWithRetry = async (params, maxRetries = 2) => {
    let attempt = 0;
    await delay(400);
    while (attempt <= maxRetries) {
      try {
        return await GenerateDraftWithLLM(params);
      } catch (err) {
        if (err?.code === 'deadline-exceeded') throw err;
        if (attempt === maxRetries) throw err;
        await delay(1000 * (attempt + 1));
        attempt += 1;
      }
    }
  };

  try {
    await runTransaction(db, async (transaction) => {
      const snap = await transaction.get(reportContentRef);
      if (!snap.exists()) {
        throw new Error(`reportContent/${stageKey} document does not exist`);
      }
      transaction.update(reportContentRef, {
        approvedParagraphs: {},
        [`${prefix}ReportApproved`]: false,
        [`${prefix}ReportApprovedBy`]: null,
        [`${prefix}ReportApprovedByName`]: null,
        [`${prefix}ReportApprovedAt`]: null,
        lastReviewedAt: serverTimestamp(),
      });
    });

    appendReportAuditEntry(patientId, {
      module: prefix,
      eventType: 'approvalReset',
      isFirstOccurrence: false,
    });

    // Sequential (not parallel) calls keep peak LLM concurrency low, matching
    // the autism utility's behaviour.
    for (const sectionTitle of sectionTitles) {
      await callWithRetry({
        clinicianId,
        patientId,
        reportStage,
        reportSection: sectionTitle,
        isTest: false,
        conditions,
        force_regenerate_keys: ['__ALL__'],
      });
    }

    return { success: true };
  } catch (error) {
    return { success: false, error };
  }
};
